import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from '@dr.pogodin/react-helmet'
import logo from '../../assets/main/logo.d99exch.png'
import './style.css'

import { authService } from '../../services/authService'
import { updateUser } from '../../store/slices/authSlice'
import { DESKTOP_VIEWPORT } from '../../hooks/useViewport'
import Toast from '../../utils/toast'

function ResetPassword() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user, role, isAuthenticated } = useSelector((state) => state.auth)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [errors, setErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    general: ''
  })

  // Only reachable while a password change is actually pending
  useEffect(() => {
    if (sessionStorage.getItem('needPasswordChange') !== 'true') {
      navigate(isAuthenticated ? '/admin/market-analysis' : '/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Leaving this page any way other than submitting the form (browser back OR a
  // page refresh) must land on the login page with the session fully dropped.
  // The hard redirect via window.location bypasses React Router, so its own
  // popstate handling can't re-render this page (the needPasswordChange guard
  // would otherwise bounce right back here).
  useEffect(() => {
    const dropSessionAndGoToLogin = () => {
      sessionStorage.removeItem('needPasswordChange')
      localStorage.removeItem('token')
      localStorage.removeItem('ownerToken')
      localStorage.removeItem('staffToken')
      localStorage.removeItem('executiveToken')
      localStorage.removeItem('executivePermissions')
      localStorage.removeItem('activeRole')
      localStorage.removeItem('user')
      window.location.replace(`${import.meta.env.BASE_URL}login`)
    }

    // Refresh: only bounce to login when the RELOAD itself happened on this
    // page (navEntry.name is the URL that was loaded). A session that merely
    // started with a reload elsewhere (e.g. F5 on the login page) and then
    // SPA-navigated here must NOT be treated as a change-password refresh.
    const navEntry = performance.getEntriesByType('navigation')[0]
    if (navEntry && navEntry.type === 'reload' && navEntry.name.includes('/reset-password')) {
      dropSessionAndGoToLogin()
      return
    }

    // Back button: push a sentinel history entry so back triggers popstate here.
    window.history.pushState(null, '', window.location.href)
    window.addEventListener('popstate', dropSessionAndGoToLogin)
    return () => window.removeEventListener('popstate', dropSessionAndGoToLogin)
  }, [])

  const validateField = (name, value) => {
    let error = ''
    
    if (name === 'oldPassword') {
      if (!value || value.trim() === '') {
        error = 'The old password field is required'
      }
    } else if (name === 'newPassword') {
      if (!value || value.trim() === '') {
        error = 'The new password field is required'
      } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
        error = 'The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number'
      } else if (value.length < 8) {
        error = 'The new password field must be at least 8 characters'
      }
    } else if (name === 'confirmPassword') {
      if (!value || value.trim() === '') {
        error = 'The confirm password field is required'
      } else if (value !== formData.newPassword) {
        error = 'Passwords do not match'
      }
    }
    
    return error
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    
    setFormData({
      ...formData,
      [name]: value
    })

    // Real-time validation
    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isSubmitting) return
    
    // Validate all fields
    const oldPasswordError = validateField('oldPassword', formData.oldPassword)
    const newPasswordError = validateField('newPassword', formData.newPassword)
    const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword)
    
    setErrors({
      oldPassword: oldPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
      general: ''
    })

    // If no errors, proceed
    if (!oldPasswordError && !newPasswordError && !confirmPasswordError) {
      setIsSubmitting(true)
      try {
        // Endpoint follows the logged-in role: owner and staff have separate ones
        const response = await authService.changePassword({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        }, role)

        if (response?.success) {
          // Original: success page ke saath top-right toast
          Toast.fire({ icon: 'success', title: 'Password change successful.' })
          sessionStorage.removeItem('needPasswordChange')
          dispatch(updateUser({ ...user, first_login: true }))
          const txnPass = response.transactionPassword || response.data?.transactionPassword || ''
          navigate('/password-success', {
            replace: true,
            state: { transactionPassword: txnPass }
          })
          return
        }

        setErrors({
          ...errors,
          general: response?.message || 'Failed to update password'
        })
      } catch (error) {
        console.error('Error updating password:', error)
        setErrors({
          ...errors,
          general: error.response?.data?.message || 'An error occurred while updating password'
        })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <>
      <Helmet>
        <meta name="viewport" content={DESKTOP_VIEWPORT} />
      </Helmet>
      <section className="login-mn">
        <div className="log-logo m-b-18">
          <img src={logo} alt="DIAMOND EXCH 99" style={{ maxWidth: '250px', maxHeight: '100px' }} />
        </div>
        <div className="log-fld">
          <h2 className="text-center">Change Password</h2>
          <form 
            autoComplete="off" 
            data-vv-scope="form-reset-password" 
            action="" 
            method="POST" 
            className="form-horizontal"
            onSubmit={handleSubmit}
          >
            {errors.general && (
              <div className="form-group">
                <span className="error">{errors.general}</span>
              </div>
            )}
            <div id="input-group-1" role="group" className="form-group">
              <div>
                <input 
                  id="input-1" 
                  name="oldPassword" 
                  type="password" 
                  placeholder="Old Password" 
                  className="form-control"
                  autoComplete="current-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.oldPassword && <span className="error">{errors.oldPassword}</span>}
              </div>
            </div>
            <div id="input-group-2" role="group" className="form-group">
              <div>
                <input 
                  id="input-2" 
                  name="newPassword" 
                  type="password" 
                  placeholder="New Password" 
                  className="form-control form-control"
                  autoComplete="new-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={formData.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.newPassword && <span className="error">{errors.newPassword}</span>}
              </div>
            </div>
            <div id="input-group-3" role="group" className="form-group">
              <div>
                <input 
                  id="input-3" 
                  name="confirmPassword" 
                  type="password" 
                  placeholder="Confirm Password" 
                  className="form-control form-control"
                  autoComplete="new-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.confirmPassword && <span className="error">{errors.confirmPassword}</span>}
              </div>
            </div>
            <div className="form-group text-center">
              <button 
                type="submit" 
                className="btn btn btn-submit btn-login btn-primary"
                disabled={isSubmitting}
              >
                Change Password <i className="fas fa-sign-in-alt"></i>
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  )
}

export default ResetPassword

